import React from 'react';
import { styled } from '@mui/material';
import Confetti from 'react-confetti';

import { useViewport } from 'contexts/viewport';
import withResponsiveLayout from 'hoc/withResponsive';
import { Box, Button, Icon, ResponsiveDialog, Stack, Text } from 'shell/components';
import GuidedTourCompleteImage from 'utils/assetFiles/svgs/GuidedTourCompleteImage';

const Mobile = ({ handleClose, handleReset }) => {
    return (
        <TourMobileBox>
            <TourMobileTopStack>{GuidedTourCompleteImage}</TourMobileTopStack>
            <TourMobileBottomStack alignItems={'center'} justifyContent={'space-between'}>
                <TourLeftComponentStack alignItems={'center'} justifyContent={'space-between'} spacing={8}>
                    <TourMobileTextStack
                        alignSelf={'center'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        spacing={2}
                    >
                        <Text variant={'h4'}>You’re Ready to Start Collaborating </Text>
                        <Text variant={'p2'}>
                            Congratulations! You are now equipped with all the tools and information you need to get
                            started using Ensightful. We are thrilled to have you here and can't wait to see all the
                            amazing things you and your team will accomplish.
                        </Text>
                    </TourMobileTextStack>
                    <Stack alignItems={'center'} spacing={5}>
                        <Button variant={'primary'} onClick={() => handleClose()}>
                            Start Using Ensightful
                        </Button>
                        <Button
                            variant={'borderless'}
                            startIcon={<Icon kind={'refresh'} />}
                            onClick={() => handleReset()}
                        >
                            Take the Tour Again
                        </Button>
                    </Stack>
                </TourLeftComponentStack>
            </TourMobileBottomStack>
        </TourMobileBox>
    );
};

const Tablet = ({ handleClose, handleReset }) => {
    return (
        <TourBox>
            <TourMobileTopStack>{GuidedTourCompleteImage}</TourMobileTopStack>
            <TourMobileBottomStack alignItems={'center'} justifyContent={'space-between'}>
                <TourLeftComponentStack alignItems={'center'} justifyContent={'space-between'} spacing={8}>
                    <TourMobileTextStack
                        alignSelf={'center'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        spacing={2}
                    >
                        <Text variant={'h4'}>You’re Ready to Start Collaborating </Text>
                        <Text variant={'p2'}>
                            Congratulations! You are now equipped with all the tools and information you need to get
                            started using Ensightful. We are thrilled to have you here and can't wait to see all the
                            amazing things you and your team will accomplish.
                        </Text>
                    </TourMobileTextStack>
                    <Stack alignItems={'center'} spacing={5}>
                        <Button variant={'primary'} onClick={() => handleClose()}>
                            Start Using Ensightful
                        </Button>
                        <Button
                            variant={'borderless'}
                            startIcon={<Icon kind={'refresh'} />}
                            onClick={() => handleReset()}
                        >
                            Take the Tour Again
                        </Button>
                    </Stack>
                </TourLeftComponentStack>
            </TourMobileBottomStack>
        </TourBox>
    );
};

const Desktop = ({ handleClose, handleReset }) => {
    return (
        <TourBox>
            <TourMobileTopStack>{GuidedTourCompleteImage}</TourMobileTopStack>
            <TourMobileBottomStack alignItems={'center'} justifyContent={'space-between'}>
                <TourLeftComponentStack alignItems={'center'} justifyContent={'space-between'} spacing={8}>
                    <TourMobileTextStack
                        alignSelf={'center'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        spacing={2}
                    >
                        <Text variant={'h4'}>You’re Ready to Start Collaborating </Text>
                        <Text variant={'p2'}>
                            Congratulations! You are now equipped with all the tools and information you need to get
                            started using Ensightful. We are thrilled to have you here and can't wait to see all the
                            amazing things you and your team will accomplish.
                        </Text>
                    </TourMobileTextStack>
                    <Stack alignItems={'center'} spacing={5}>
                        <Button variant={'primary'} onClick={() => handleClose()}>
                            Start Using Ensightful
                        </Button>
                        <Button
                            variant={'borderless'}
                            startIcon={<Icon kind={'refresh'} />}
                            onClick={() => handleReset()}
                        >
                            Take the Tour Again
                        </Button>
                    </Stack>
                </TourLeftComponentStack>
            </TourMobileBottomStack>
        </TourBox>
    );
};

const TourCompletionModal = ({ open, handleClose, handleReset }) => {
    const { device, height, width } = useViewport();

    const props = {
        device,
        handleClose,
        handleReset,
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    return (
        <>
            <CustomDialog sx={{ padding: 0 }} closable device={device} open={open} onClose={handleClose}>
                {withResponsiveLayout(width, responsiveLayouts)}
            </CustomDialog>
            <Confetti numberOfPieces={200} width={width} height={height - 64} run={open} style={{ zIndex: 10000 }} />
        </>
    );
};

const CustomDialog = styled(ResponsiveDialog)(({ device }) => {
    return {
        '& .MuiBox-root': {
            padding: 0,
        },
        ...(device === 'mobile' && {
            '& .MuiPaper-root > .MuiBox-root': {
                height: '100%',
            },
        }),
    };
});

const TourBox = styled(Box)(() => {
    return {
        padding: 0,
        overflow: 'hidden',
        width: '634px',
        height: '611px',
    };
});

const TourLeftComponentStack = styled(Stack)(({ theme }) => {
    return {
        paddingBottom: theme.spacing(8),
        height: '100%',
    };
});

const TourMobileBox = styled(Box)(() => {
    return {
        padding: 0,
        width: '100%',
        height: '100%',
    };
});

const TourMobileTopStack = styled(Stack)(({ theme }) => {
    return {
        height: '50%',
        background: theme.palette.ensightful.primary[50],
    };
});

const TourMobileBottomStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(8, 4),
        height: '50%',
    };
});

const TourMobileTextStack = styled(Stack)(() => {
    return {
        textAlign: 'center',
        width: '90%',
    };
});

export default TourCompletionModal;
